import {
  FlowNodeData,
  PromptVariable,
  SettingParam,
  VariableTypes,
} from "@/types/node";
import {useState, useEffect} from "react";
import React from "react";
import {getActionsFromNodes} from "@/utils/index";
import {Node, useReactFlow} from "@xyflow/react";
import SlatePromptEditor from "@/components/settings/PromptEditor/SlatePromptEditor";
import {getVariableParams, useVariables} from "@/contexts/VariablesContext";
import {useSubflows} from "@/contexts/SubflowsContext";
import {Tooltip, TooltipContent, TooltipTrigger} from "../ui/tooltip";
import {Icon} from "../ui/icon";
import {Info} from "lucide-react";

export const highlightVariables = (
  text: string,
): React.ReactNode[] | string => {
  if (!text) return text;

  const variableParams = getVariableParams();

  // Build a dynamic regex pattern from variableParams
  const regexPattern = Object.values(variableParams)
    .map(({open, close}) =>
      close
        ? `\\${open}[^\\${open}\\${close}]*\\${close}`
        : `\\${open}[^\\${open}]*\\s`,
    )
    .join("|");

  const regex = new RegExp(regexPattern, "g");

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let matchCount = 0;

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Process the matched variable
    const cleanMatch = match[0].replace(/\u200B/g, ""); // Remove zero-width spaces

    // Find the corresponding variable type based on brackets
    const matchedType = Object.keys(variableParams).find(
      type =>
        cleanMatch.startsWith(variableParams[type].open) &&
        cleanMatch.endsWith(variableParams[type].close),
    );

    if (matchedType) {
      const {open, close} = variableParams[matchedType];
      const content =
        cleanMatch
          .slice(open.length, cleanMatch.length - close.length)
          .trim() || "\u200B"; // Keep empty markers visible with zero-width space

      parts.push(
        <mark
          key={`variable-${matchCount}-${match.index}`}
          spellCheck={false}
          className="inline-block border px-1 pt-0 pb-0.5 rounded-md bg-white hover:bg-slate-100 pointer-events-none"
        >
          <i className="not-italic font-semibold mr-1 text-primary-accent">
            {open}
          </i>
          <span>{content}</span>
          <i className="not-italic font-semibold ml-1 text-primary-accent">
            {close}
          </i>
        </mark>,
      );
      matchCount++;
    } else {
      // If no match found, add original text
      parts.push(match[0]);
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  // If no variables were found, return the original text
  return parts.length === 0 ? text : parts;
};

export const cleanupSearchParam = (searchParam: string) => {
  return searchParam.split(":")[0];
};

export const SettingPrompt = ({
  param,
  handleChange,
  selectedNodeId,
  disabled = false,
  tooltipMessage,
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: string) => void;
  selectedNodeId: string;
  disabled: boolean;
  tooltipMessage?: string;
}) => {
  const {getNodes} = useReactFlow();
  const nodes = getNodes();
  const [value, setValue] = useState(param.value?.toString() || "");
  const variableParams = getVariableParams();
  const {getSelectedSubflowVariables} = useVariables();
  const {selectedSubflowId} = useSubflows();
  const variables = getSelectedSubflowVariables(selectedSubflowId || "main");

  const getVariablesData = (
    searchParam: string,
    type: VariableTypes,
  ): PromptVariable[] => {
    if (type === VariableTypes.VARIABLES) {
      const capturedVariables = variables
        .map(variable => variable.name)
        .filter(variable => variable.includes(searchParam));
      return capturedVariables;
    }
    if (type === VariableTypes.ACTIONS || type === VariableTypes.RESULTS) {
      const actions = getActionsFromNodes(nodes as Node<FlowNodeData>[]);

      if (type === VariableTypes.RESULTS) {
        return actions.filter(
          action =>
            Array.isArray(action.action_results) &&
            action.action_results.length > 0,
        );
      }
      return actions;
    }
  };

  useEffect(() => {
    setValue(param.value?.toString() || "");
  }, [param, selectedNodeId]);

  return (
    <div className="mb-4">
      <label className="text-sm font-medium text-label mb-2 flex items-center justify-start">
        {param.label}
        {tooltipMessage && (
          <Tooltip delayDuration={0} disableHoverableContent={true}>
            <TooltipTrigger asChild>
              <Icon className="w-4 h-4 border-0 text-disabled ml-1 cursor-pointer">
                <Info />
              </Icon>
            </TooltipTrigger>
            <TooltipContent
              side="left"
              sideOffset={10}
              className="max-w-72 text-left"
            >
              <div className="flex flex-col gap-1">
                <div>{tooltipMessage}</div>
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </label>

      <div className="relative">
        <SlatePromptEditor
          value={value}
          placeholder={param.placeholder}
          variableParams={variableParams}
          onChange={value => {
            setValue(value);
            handleChange(param, value);
          }}
          getVariablesData={getVariablesData}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
